import TableHead from '@mui/material/TableHead';
import Checkbox from '@mui/material/Checkbox';
import { TableCellBody, TableCellHead, TableRow } from '../common/Table';
import TableBody from '@mui/material/TableBody';
import { BoldTypography, BoldTypographyLarge } from '../common/TextLabel';
import Moment from 'react-moment';
import Table from '@mui/material/Table';
import { decode } from 'he';
import { capitalise } from '../../utils/common';
import plusButton from '../../icons/plus.png';
import removeButton from '../../icons/remove.png';
import { Grid, SvgIcon, Tooltip } from '@mui/material';
import { PlaylistVideo } from '../types/playlist';
import copyToClipboard from '../utils/copyToClipboard';
import { ReactComponent as CopyContentIcon } from '../../icons/copy-content.svg';

export const PlaylistVideoTable = ({
  videos,
  onSetVideoPreview,
  onSetAddVideoPreview,
  onRemoveVideo,
  onAssignAdBreakType,
  onSkipCustomCaption,
  format,
}: {
  format: string,
  videos: PlaylistVideo[],
  onSetVideoPreview: (data: PlaylistVideo) => void,
  onSetAddVideoPreview: (data: PlaylistVideo | {}) => void,
  onRemoveVideo: (id: number) => void,
  onSkipCustomCaption: (id: number, val: boolean) => void,
  onAssignAdBreakType: (id: number, val: string | null, skippable: boolean) => void,
}) => {
  const isPublisherView = localStorage.getItem('isPublisherView') === 'true';
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCellHead>PLAYS</TableCellHead>
          <TableCellHead>VIDEO</TableCellHead>
          <TableCellHead>DATE</TableCellHead>
          <TableCellHead sx={{minWidth: '280px'}}>TITLE</TableCellHead>
          <TableCellHead>RIGHTSHOLDER</TableCellHead>
          <TableCellHead>DURATION</TableCellHead>
          <TableCellHead sx={{ whiteSpace: 'nowrap'}}>ADDED DATE</TableCellHead>
          <TableCellHead>DISPLAY AS PREROLL</TableCellHead>
          <TableCellHead>PREROLL SKIPPABLE</TableCellHead>
          <TableCellHead>SKIP CUSTOM CAPTION</TableCellHead>
          {!isPublisherView && <TableCellHead width="160">TOOLS</TableCellHead>}
        </TableRow>
      </TableHead>
      <TableBody>
        {videos.length === 0 && (
          <TableRow>
            <TableCellBody colSpan={10} width={'100%'}>
              <div style={{ textAlign: 'center', padding: 20 }}>This playlist contains no videos yet</div>
            </TableCellBody>
            {!isPublisherView && (
              <TableCellBody sx={{ minWidth: '200px' }}>
                <span onClick={() => onSetAddVideoPreview({})} style={{ cursor: 'pointer' }}>
                  <img width="28" src={plusButton} alt="plus button" />
                </span>
              </TableCellBody>
            )}
          </TableRow>
        )}
        {videos &&
          videos.map(row => (
            <TableRow
              sx={{
                height: '60px',
                backgroundColor: row.video_format !== format ? '#f2feff' : (row.has_trending_matched ? 'lightyellow' : 'transparent')
              }}
              key={row.id}
            >
              <TableCellBody align="center">{row.video_play_count.toLocaleString()}</TableCellBody>
              <TableCellBody
                sx={{ width: '104px' }}
                onClick={() => {
                  onSetVideoPreview(row);
                }}
              >
                <div
                  style={{
                    backgroundImage: `url(${row.video_thumbnail || '/bg-grey.png'})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    borderRadius: '5%',
                    aspectRatio: format === 'vertical' ? '9 / 16' : '4 / 3',
                    position: 'relative'
                  }}
                >
                  { row.is_opus && <img src="/logo/pixels.png" style={{
                    width: '20px',
                    borderRadius: '4px',
                    position: 'absolute',
                    top: '6px',
                    right: '6px',
                  }} alt=""/> }
                </div>
              </TableCellBody>
              <TableCellBody>
                <BoldTypography>
                  <Moment format="Do MMM">{row.video_publish_date}</Moment>
                </BoldTypography>
              </TableCellBody>
              <TableCellBody><span title={row.video_title_translated}>{decode(row.video_title)}</span></TableCellBody>
              <TableCellBody sx={{ minWidth: '150px' }}>
                <BoldTypographyLarge>{capitalise(row.video_rightsholder)}</BoldTypographyLarge>
              </TableCellBody>
              <TableCellBody>{row.video_duration}</TableCellBody>
              <TableCellBody>
                <BoldTypography>
                  <Moment format="Do MMM">{row.record_inserted_at}</Moment>
                </BoldTypography>
              </TableCellBody>
              <TableCellBody>
                <Checkbox
                  checked={Boolean(row.ad_break_type)}
                  size="small"
                  onChange={({ target: { checked }}) => onAssignAdBreakType(row.id, checked ? 'preroll' : null, row.ad_break_skippable)}
                />
              </TableCellBody>
              <TableCellBody>
                <Checkbox
                  checked={row.ad_break_skippable}
                  disabled={row.ad_break_type !== 'preroll'}
                  size="small"
                  onChange={({ target: { checked }}) => onAssignAdBreakType(row.id, row.ad_break_type, checked)}
                />
              </TableCellBody>
              <TableCellBody>
                <Tooltip
                  title={row.video_disable_custom_caption ? 'Custom caption is disabled because' +
                    ' it has been disabled by the video owner' : ''}>
                  <span>
                    <Checkbox
                      checked={row.video_disable_custom_caption || row.skip_custom_caption}
                      size="small"
                      disabled={row.video_disable_custom_caption}
                      onChange={({ target: { checked } }) => onSkipCustomCaption(row.id, checked)}
                    />
                  </span>
                </Tooltip>
              </TableCellBody>
              {!isPublisherView && (
                <TableCellBody sx={{ minWidth: '200px' }}>
                  <Grid container alignItems={'center'} spacing={2}>
                    <Grid item title={'copy media id'}>
                      <SvgIcon sx={{ cursor: 'pointer'}} onClick={copyToClipboard(row.video_id)}>
                        <CopyContentIcon />
                      </SvgIcon>
                    </Grid>
                    <Grid item>
                      <span onClick={() => onSetAddVideoPreview(row)} style={{ cursor: 'pointer' }}>
                        <img width="28" src={plusButton} alt="plus button" />
                      </span>
                    </Grid>
                    <Grid item>
                      <span onClick={() => onRemoveVideo(row.id)} style={{ cursor: 'pointer' }}>
                        <img width="28" src={removeButton} alt="remove button" />
                      </span>
                    </Grid>
                  </Grid>
                </TableCellBody>
              )}
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
};
